import './demand-line-item.css';

import { Badge } from '@kerfjs/ui/badge';
import { Button } from '@kerfjs/ui/button';
import { ContentItem } from '@kerfjs/ui/content-item';
import { rem } from '@kerfjs/ui/css-values';
import { List } from '@kerfjs/ui/list';
import { LucideIcon } from '@kerfjs/ui/lucide-icon';
import { Row } from '@kerfjs/ui/row';
import { Spacer } from '@kerfjs/ui/spacer';
import { Text } from '@kerfjs/ui/text';
import { type SafeHtml } from 'kerfjs';
import { Info, MessageSquareDot, ScanSearch, TriangleAlert } from 'lucide';

import { lineReviewFindings, reviewCitationLabel } from '../../shared/quotation-review.js';
import type { AnalysisReport, DecisionT, LineOrderT, QuoteMatrix } from '../data/api.js';
import type { IntakeLine } from '../data/state.js';
import { lineClarificationReasons, lineDetailHints } from '../lib/line-status.js';
import { DemandLineItemAnalysis } from './demand-line-item-analysis.js';
import { DemandLineItemDecision } from './demand-line-item-decision.js';
import { DemandLineItemMain } from './demand-line-item-main.js';
import { DemandLineItemOrder } from './demand-line-item-order.js';
import { DemandQuoteItem } from './demand-quote-item.js';

export function DemandLineItem({
  demandId,
  line,
  selected,
  analysis,
  decision,
  matrix,
  status,
  readOnly = false,
  canDecide = true,
  decisionsLocked = readOnly,
  unreadNoteCount = 0,
  clarificationQuestion,
  order,
  failedOrder,
}: {
  demandId?: string;
  line: IntakeLine;
  selected: boolean;
  analysis: AnalysisReport | null;
  decision: (Partial<DecisionT> & Pick<DecisionT, 'action'>) | undefined;
  matrix: QuoteMatrix | null;
  status: SafeHtml;
  readOnly?: boolean;
  canDecide?: boolean;
  /** Decisions stay open on a decided demand whose lines are locked (HS2-Y50H8E). */
  decisionsLocked?: boolean;
  /** Notes on this line by others since the viewer last opened it (HS2-JRQT8A). */
  unreadNoteCount?: number;
  /** The reviewer's open Needs Clarification question on this line (HS2-4YCV66). */
  clarificationQuestion?: string;
  /** The line's current purchase order (FR-5.18, HS2-HYDRJX). */
  order?: LineOrderT;
  /** The newest failed order while the line is re-sourced (FR-5.21, HS2-SFY5DY). */
  failedOrder?: LineOrderT;
}): SafeHtml {
  const verdict = analysis?.verdicts.find((row) => row.lineItemId === line.id);
  const base = analysis?.analysis.summary?.base ?? matrix?.base ?? 'PKR';
  const normalized = line.normalizedDescription?.trim();
  const display = normalized !== undefined && normalized !== '' ? normalized : line.description;
  const quantity =
    line.quantity !== null || line.unit !== null ? `${line.quantity ?? '—'} ${line.unit ?? ''}`.trim() : '';
  const clarificationReasons = lineClarificationReasons(line, clarificationQuestion);
  // Quiet unit/quantity notes (HS2-T1N9FJ); hidden on a read-only line, where nothing can be fixed.
  const detailHints = readOnly ? [] : lineDetailHints(line);
  // The quotation review's findings about this line (FR-3.26a, HS2-XG16AM), from the selected report.
  const reviewFindings = lineReviewFindings(analysis?.analysis.summary?.review, line.id);
  return (
    // A multi-select Kerf ContentItem row (HS2-TYTAS6, Kerf KF-EYVWCR): Kerf owns
    // the grid row/gridcell semantics, frame, hover, focus, and selected paint, and
    // its nested controls (row menu, quote chips, decision controls) keep their
    // own actions. init.ts owns selection, Enter-to-edit, and double-click edit.
    <ContentItem
      interactive
      action="line-select"
      itemId={line.id}
      selectionMode="multiple"
      selected={selected}
      frame="framed"
      title={line.description}
      className="demand-line-item"
      rootAttributes={{ 'data-line-item': line.id, 'data-key': line.id }}
    >
      <List gap="xs">
        <DemandLineItemMain
          demandId={demandId}
          lineId={line.id}
          lineNumber={line.lineNumber}
          title={display}
          quantity={quantity}
          status={status}
          currentAction={decision?.action}
          readOnly={readOnly}
          canDecide={canDecide}
          decisionsLocked={decisionsLocked}
        />
        {clarificationReasons.length > 0 ? (
          <Row className="demand-line-item-warning" vAlign="baseline" gap="xs" controlInsets="rl">
            {/* Mirrors the header's number column so the reason sits under the title. */}
            <Spacer width={rem(1.5)} />
            <Text variant="span" tone="danger">
              <LucideIcon icon={TriangleAlert} name="" />
            </Text>
            {/* The reason and its Open Line share a baseline, so the action reads as part of the text. */}
            <Row vAlign="baseline" gap="xs" wrap>
              <Text variant="span" tone="danger" size="compact" role="alert">
                {clarificationReasons.join(' ')}
              </Text>
              {clarificationQuestion !== undefined ? (
                // The question's full text and the reply live in the line's notes (HS2-4YCV66).
                <Button
                  tone="danger"
                  size="compact"
                  emphasis="plain"
                  action="line-open"
                  rootAttributes={{ 'data-line-id': line.id }}
                >
                  Open Line
                </Button>
              ) : null}
            </Row>
          </Row>
        ) : null}
        {detailHints.length > 0 ? (
          <Row className="demand-line-item-hint" vAlign="baseline" gap="xs" controlInsets="rl">
            {/* Mirrors the header's number column so the hint sits under the title. */}
            <Spacer width={rem(1.5)} />
            <Text variant="span" tone="quiet">
              <LucideIcon icon={Info} name="" />
            </Text>
            {/* An inner row, as in the warning above, keeps the icon from shrinking when the text wraps. */}
            <Row vAlign="baseline" gap="xs" wrap>
              <Text variant="span" tone="quiet" size="compact">
                {detailHints.join(' ')}
              </Text>
            </Row>
          </Row>
        ) : null}
        {reviewFindings.map((finding) => (
          <Row
            className="demand-line-item-review"
            vAlign="baseline"
            gap="xs"
            controlInsets="rl"
            rootAttributes={{ 'data-review-finding': finding.kind }}
          >
            {/* Mirrors the header's number column, like the hint above. */}
            <Spacer width={rem(1.5)} />
            {/* A rigid box keeps the icon full size while the note wraps at phone width. */}
            <Row flex="none">
              <Text variant="span" tone={finding.severity === 'high' ? 'danger' : 'quiet'}>
                <LucideIcon icon={ScanSearch} name="" />
              </Text>
            </Row>
            <Row vAlign="baseline" gap="xs" wrap>
              <Text variant="span" tone="quiet" size="compact">
                {`Quotation review: ${finding.message}${finding.citations.length > 0 ? ` (${finding.citations.map(reviewCitationLabel).join('; ')})` : ''}`}
              </Text>
            </Row>
          </Row>
        ))}
        <DemandQuoteItem
          matrix={matrix}
          lineId={line.id}
          chosenQuoteLineId={decision?.chosenQuoteLineId ?? null}
          readOnly={decisionsLocked}
        />
        <DemandLineItemAnalysis
          verdict={verdict}
          base={base}
          quantity={line.quantity}
          lineId={line.id}
          hs={{
            code: line.hsCode ?? null,
            dutyPct: line.hsCodeDutyPct ?? null,
            confirmed: (line.hsCodeConfirmedAt ?? null) !== null,
          }}
        />
        <DemandLineItemDecision decision={decision} fallbackCurrency={base} />
        <DemandLineItemOrder order={order} failed={failedOrder} />
        {unreadNoteCount > 0 ? (
          // A status line at the bottom of the row: opening the line reads its notes (HS2-JRQT8A).
          <Row className="demand-line-item-unread-notes" vAlign="middle" gap="xs" controlInsets="rl">
            <Spacer width={rem(1.5)} />
            <Badge
              tone="info"
              appearance="quiet"
              shape="pill"
              size="compact"
              icon={<LucideIcon icon={MessageSquareDot} name="" />}
            >
              {`${String(unreadNoteCount)} unread note${unreadNoteCount === 1 ? '' : 's'}`}
            </Badge>
          </Row>
        ) : null}
      </List>
    </ContentItem>
  );
}
